import { useApprovals } from '@servicetim/ui/api';
import { Badge, Button } from '@servicetim/ui/components';
import { useState } from 'react';
import { ApprovalDecisionDialog, type ApprovalDecisionTarget } from '../approvals/decision-dialog';
import { formatDateTime } from './format';
import { RailCard, RailRow } from './rail-card';
import { useTicketContext } from './ticket-context';

/**
 * Pending/decided approvals on this ticket where the caller is (or was) an approver, with
 * Approve/Reject (S-07-09). **Known gap**: there is no `GET /v1/tickets/{id}/approvals`
 * endpoint — only `GET /v1/approvals?mine=true`, one approver's own rows — so a viewer who is
 * not an approver on this ticket's request sees nothing here, and other approvers' rows never
 * show. Reported to H2/wave-4; not something this UI can work around.
 */
export function ApprovalsCard() {
  const { ticket, meId } = useTicketContext();
  const [target, setTarget] = useState<ApprovalDecisionTarget | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const pending = useApprovals({ mine: true, decision: 'pending' }, 200);
  const approved = useApprovals({ mine: true, decision: 'approved' }, 200, {
    enabled: showHistory,
  });
  const rejected = useApprovals({ mine: true, decision: 'rejected' }, 200, {
    enabled: showHistory,
  });

  const forThisTicket = (items: typeof pending.items) =>
    items.filter((a) => a.ticketId === ticket.id);
  const pendingHere = forThisTicket(pending.items);
  const decidedHere = showHistory
    ? [...forThisTicket(approved.items), ...forThisTicket(rejected.items)]
    : [];

  if (pending.isPending) return null;
  if (pendingHere.length === 0 && !showHistory) {
    return (
      <RailCard
        title="Approvals"
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowHistory(true)}>
            Show history
          </Button>
        }
      >
        <p className="text-fg-muted text-sm">Nothing pending for you on this ticket.</p>
      </RailCard>
    );
  }

  return (
    <RailCard
      title="Approvals"
      actions={
        !showHistory ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowHistory(true)}>
            Show history
          </Button>
        ) : null
      }
    >
      <ul className="space-y-3">
        {[...pendingHere, ...decidedHere].map((a) => (
          <li key={a.id} className="space-y-1.5">
            <RailRow label="Status">
              <Badge
                variant={
                  a.decision === 'pending'
                    ? 'warning'
                    : a.decision === 'approved'
                      ? 'success'
                      : 'destructive'
                }
              >
                {a.decision}
              </Badge>
              <span className="ml-2 text-fg-muted text-xs">
                {a.mode === 'all' ? 'all must approve' : 'any one approves'}
              </span>
            </RailRow>
            {a.decidedAt ? (
              <RailRow label="Decided">
                <span className="text-fg-muted text-xs">{formatDateTime(a.decidedAt)}</span>
              </RailRow>
            ) : null}
            {a.comment ? (
              <RailRow label="Comment">
                <span className="text-fg-muted text-xs">{a.comment}</span>
              </RailRow>
            ) : null}
            {a.decision === 'pending' && a.approverId === meId ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setTarget({
                      ticketId: ticket.id,
                      approvalId: a.id,
                      decision: 'rejected',
                      ticketKey: ticket.key,
                    })
                  }
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    setTarget({
                      ticketId: ticket.id,
                      approvalId: a.id,
                      decision: 'approved',
                      ticketKey: ticket.key,
                    })
                  }
                >
                  Approve
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <ApprovalDecisionDialog target={target} onClose={() => setTarget(null)} />
    </RailCard>
  );
}
